import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AppLayout from "../../shared/components/AppLayout";
import { API_BASE } from "../../utils/api";

interface PendingRegistration {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  created_at: string;
}

const RegistrationApprovalsPage: React.FC = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");

  const [registrations, setRegistrations] = useState<PendingRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);


  const showToast = (msg: string, type: "success" | "error") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_BASE}/users/pending-registrations/`);
      setRegistrations(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || "Failed to load registrations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (id: number) => {
    setActionLoading(id);
    try {
      await axios.post(`${API_BASE}/users/approve-registration/`, { id });
      showToast("Registration approved. Welcome email sent.", "success");
      setRegistrations((prev) => prev.filter((r) => r.id !== id));
    } catch (err: any) {
      showToast(err.response?.data?.error || "Approval failed.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const reject = async (id: number) => {
    if (!window.confirm("Reject this registration? The applicant will be notified.")) return;
    setActionLoading(id);
    try {
      await axios.post(`${API_BASE}/users/reject-registration/`, { id });
      showToast("Registration rejected.", "success");
      setRegistrations((prev) => prev.filter((r) => r.id !== id));
    } catch (err: any) {
      showToast(err.response?.data?.error || "Rejection failed.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <AppLayout active="admin-registrations">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Pending Registrations</h1>
              <p className="text-sm text-gray-500 mt-1">
                Review and approve or reject doctor registration requests.
              </p>
            </div>

            {toast && (
              <div
                className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
                  toast.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {toast.msg}
              </div>
            )}

            {loading && (
              <div className="text-center py-16 text-gray-400">Loading...</div>
            )}

            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            {!loading && !error && registrations.length === 0 && (
              <div className="text-center py-16 text-gray-400">
                No pending registrations.
              </div>
            )}

            {!loading && registrations.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">Name</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">Email</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">Phone</th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">Submitted</th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {registrations.map((reg) => (
                      <tr key={reg.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 font-medium text-gray-900">
                          {reg.first_name} {reg.last_name}
                        </td>
                        <td className="px-6 py-4 text-gray-600">{reg.email}</td>
                        <td className="px-6 py-4 text-gray-600">{reg.phone_number}</td>
                        <td className="px-6 py-4 text-gray-500">
                          {new Date(reg.created_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 justify-end">
                            <button
                              onClick={() => approve(reg.id)}
                              disabled={actionLoading === reg.id}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition"
                            >
                              {actionLoading === reg.id ? "..." : "Approve"}
                            </button>
                            <button
                              onClick={() => reject(reg.id)}
                              disabled={actionLoading === reg.id}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 transition"
                            >
                              {actionLoading === reg.id ? "..." : "Reject"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
    </AppLayout>
  );
};

export default RegistrationApprovalsPage;
